import React, { useMemo, useState, useEffect } from "react";
import { collectionGroup, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { FaBalanceScale, FaBook, FaChartLine, FaSearch } from "react-icons/fa";
import { db } from "../firebase";
import { formatDateTime } from "../hooks/useWalletAdminData";
import { canViewWalletModule } from "../utils/walletPermissions";
import "./walletAdmin.css";

function money(value) {
  return Number(value || 0).toFixed(2);
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function trnFor(row) {
  if (row.transactionNo || row.trn) return row.transactionNo || row.trn;
  const seed = String(row.id || "").replace(/[^0-9]/g, "");
  const padded = (seed || String(toMillis(row.createdAt))).slice(-12).padStart(12, "0");
  return `TRN${padded}`;
}

function normalizeTx(docSnap) {
  const data = docSnap.data() || {};
  const phone = docSnap.ref.parent.parent?.id || "";
  const amount = Number(data.amount || 0);
  const direction = String(data.direction || "").toLowerCase();
  const type = String(data.type || data.sourceType || "").toLowerCase();
  const isDebit = direction === "debit";
  const isCredit = direction === "credit";

  return {
    id: docSnap.id,
    phone,
    trn: trnFor({ id: docSnap.id, ...data }),
    title: data.title || data.type || "Wallet transaction",
    type,
    sourceType: String(data.sourceType || data.meta?.sourceType || ""),
    direction: isDebit ? "debit" : "credit",
    credit: isCredit ? amount : 0,
    debit: isDebit ? amount : 0,
    amount,
    status: String(data.status || "success"),
    note: String(data.adminNote || data.note || data.reason || ""),
    requestId: String(data.requestId || data.meta?.requestId || ""),
    paymentId: String(data.paymentId || data.meta?.paymentId || ""),
    createdBy: data.createdBy || {},
    createdAt: data.createdAt || null,
    raw: data,
  };
}

function dayKey(row) {
  const ms = toMillis(row.createdAt);
  if (!ms) return "No Date";
  return new Date(ms).toLocaleDateString("en-IN");
}

function classify(row) {
  const hay = `${row.type} ${row.title} ${row.sourceType}`.toLowerCase();
  if (hay.includes("withdraw") || hay.includes("settlement") || hay.includes("payout")) return "payout";
  if (hay.includes("artist") || hay.includes("mentor") || hay.includes("earning")) return "creator";
  if (hay.includes("event") || hay.includes("booking") || hay.includes("ticket")) return "event";
  if (hay.includes("song") || hay.includes("announcement")) return "song";
  if (hay.includes("admin")) return "admin";
  return "general";
}

export default function WalletAudit() {
  const allowed = canViewWalletModule();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("ledger");

  useEffect(() => {
    if (!allowed) return undefined;
    const q = query(collectionGroup(db, "wallet_transactions"), orderBy("createdAt", "desc"), limit(800));
    const unsub = onSnapshot(q, (snap) => {
      setRows(snap.docs.map(normalizeTx));
      setError("");
      setLoading(false);
    }, (err) => {
      setError(err?.message || "Failed to load wallet audit");
      setLoading(false);
    });
    return () => unsub();
  }, [allowed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const hay = `${row.trn} ${row.phone} ${row.title} ${row.type} ${row.status} ${row.note} ${row.requestId} ${row.paymentId}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const totals = useMemo(() => {
    return filtered.reduce((acc, row) => {
      acc.credit += row.credit;
      acc.debit += row.debit;
      const bucket = classify(row);
      if (bucket === "event" || bucket === "song") acc.revenue += row.credit;
      if (bucket === "creator" || bucket === "payout") acc.paidOut += row.debit;
      if (bucket === "admin") acc.adminNet += row.credit - row.debit;
      return acc;
    }, { credit: 0, debit: 0, revenue: 0, paidOut: 0, adminNet: 0 });
  }, [filtered]);

  const dailyRows = useMemo(() => {
    const map = new Map();
    filtered.forEach((row) => {
      const key = dayKey(row);
      if (!map.has(key)) map.set(key, { day: key, credit: 0, debit: 0, count: 0 });
      const item = map.get(key);
      item.credit += row.credit;
      item.debit += row.debit;
      item.count += 1;
    });
    return Array.from(map.values());
  }, [filtered]);

  if (!allowed) {
    return <div className="wallet-card wallet-page">Access denied.</div>;
  }

  return (
    <div className="wallet-page">
      <div className="wallet-card wallet-toolbar-card">
        <div className="wallet-panel-head wallet-panel-head-compact">
          <div>
            <h3 className="wallet-panel-title">Wallet Audit</h3>
            <div className="wallet-panel-copy">
              Full wallet bookkeeping ledger across admin credits/debits, creator earnings, payouts, event bookings, and wallet payment movement.
            </div>
          </div>
          <span className="wallet-chip chip-light">{filtered.length} transaction(s)</span>
        </div>

        <div className="wallet-audit-tabs">
          <button className={view === "ledger" ? "active" : ""} onClick={() => setView("ledger")}><FaBook /> Ledger</button>
          <button className={view === "daily" ? "active" : ""} onClick={() => setView("daily")}><FaChartLine /> Daily TRNs</button>
          <button className={view === "balance" ? "active" : ""} onClick={() => setView("balance")}><FaBalanceScale /> Balance Sheet</button>
          <button className={view === "pl" ? "active" : ""} onClick={() => setView("pl")}><FaChartLine /> Profit & Loss</button>
        </div>

        <div className="wallet-filter-grid" style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <FaSearch color="#64748b" />
            <input
              className="wallet-input"
              placeholder="Search TRN, phone, request, payment, note, service..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="wallet-grid wallet-grid-kpi">
        <div className="wallet-card wallet-kpi-card"><div className="wallet-kpi-title">Total Credit</div><div className="wallet-kpi-value">Rs {money(totals.credit)}</div></div>
        <div className="wallet-card wallet-kpi-card"><div className="wallet-kpi-title">Total Debit</div><div className="wallet-kpi-value">Rs {money(totals.debit)}</div></div>
        <div className="wallet-card wallet-kpi-card"><div className="wallet-kpi-title">Net Wallet Movement</div><div className="wallet-kpi-value">Rs {money(totals.credit - totals.debit)}</div></div>
        <div className="wallet-card wallet-kpi-card"><div className="wallet-kpi-title">Estimated P&L</div><div className="wallet-kpi-value">Rs {money(totals.revenue - totals.paidOut)}</div></div>
      </div>

      {loading ? <div className="wallet-card wallet-empty">Loading wallet audit...</div> : null}
      {error ? <div className="wallet-card wallet-empty">{error}</div> : null}

      {!loading && !error && view === "ledger" ? <LedgerTable rows={filtered} /> : null}
      {!loading && !error && view === "daily" ? <DailyTable rows={dailyRows} /> : null}
      {!loading && !error && view === "balance" ? <BalanceSheet totals={totals} /> : null}
      {!loading && !error && view === "pl" ? <ProfitLoss totals={totals} /> : null}
    </div>
  );
}

function LedgerTable({ rows }) {
  return (
    <div className="wallet-card wallet-table-card">
      <h3 className="wallet-panel-title">Ledger</h3>
      <div className="wallet-table-wrap">
        <table className="wallet-table wallet-table-wide">
          <thead>
            <tr>
              <th>TRN</th>
              <th>Date</th>
              <th>Wallet Phone</th>
              <th>Service</th>
              <th>Credit</th>
              <th>Debit</th>
              <th>Status</th>
              <th>Reference</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.phone}_${row.id}`}>
                <td>{row.trn}</td>
                <td>{formatDateTime(row.createdAt)}</td>
                <td>{row.phone}</td>
                <td>{row.title}</td>
                <td>Rs {money(row.credit)}</td>
                <td>Rs {money(row.debit)}</td>
                <td><span className={`wallet-chip chip-${row.status}`}>{row.status}</span></td>
                <td>{row.requestId || row.paymentId || row.id}</td>
                <td>{row.note || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DailyTable({ rows }) {
  return (
    <div className="wallet-card wallet-table-card">
      <h3 className="wallet-panel-title">Daily Transaction Register</h3>
      <div className="wallet-table-wrap">
        <table className="wallet-table">
          <thead><tr><th>Date</th><th>TRN Count</th><th>Credit</th><th>Debit</th><th>Net</th></tr></thead>
          <tbody>{rows.map((row) => <tr key={row.day}><td>{row.day}</td><td>{row.count}</td><td>Rs {money(row.credit)}</td><td>Rs {money(row.debit)}</td><td>Rs {money(row.credit - row.debit)}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

function BalanceSheet({ totals }) {
  return (
    <div className="wallet-card wallet-book-card">
      <h3 className="wallet-panel-title">Balance Sheet View</h3>
      <div className="wallet-book-row"><strong>Wallet credits/liability created</strong><span>Rs {money(totals.credit)}</span></div>
      <div className="wallet-book-row"><strong>Wallet debits/settlements paid</strong><span>Rs {money(totals.debit)}</span></div>
      <div className="wallet-book-row wallet-book-total"><strong>Net outstanding movement</strong><span>Rs {money(totals.credit - totals.debit)}</span></div>
    </div>
  );
}

function ProfitLoss({ totals }) {
  return (
    <div className="wallet-card wallet-book-card">
      <h3 className="wallet-panel-title">Profit & Loss View</h3>
      <div className="wallet-book-row"><strong>Revenue side: event/song/payment credits</strong><span>Rs {money(totals.revenue)}</span></div>
      <div className="wallet-book-row"><strong>Expense side: artist/mentor/payout debits</strong><span>Rs {money(totals.paidOut)}</span></div>
      <div className="wallet-book-row"><strong>Admin adjustment net</strong><span>Rs {money(totals.adminNet)}</span></div>
      <div className="wallet-book-row wallet-book-total"><strong>Estimated operating P&L</strong><span>Rs {money(totals.revenue - totals.paidOut + totals.adminNet)}</span></div>
    </div>
  );
}
